// GS1-128 / GS1 DataMatrix element-string parser (GS1 General Specifications §3, §7.8).
// Accepts raw scanner output (FNC1 transmitted as GS, 0x1D), an optional symbology
// identifier (]C1, ]d2, ]Q3, ]e0) and the human-readable bracketed form "(01)...(17)...".
export const GS = '\u001d';
const SPECS = {
    '00': { title: 'SSCC', length: 18, kind: 'sscc' },
    '01': { title: 'GTIN', length: 14, kind: 'gtin' },
    '02': { title: 'Content GTIN', length: 14, kind: 'gtin' },
    '10': { title: 'Batch/lot', max: 20, kind: 'alnum' },
    '11': { title: 'Production date', length: 6, kind: 'date' },
    '13': { title: 'Packaging date', length: 6, kind: 'date' },
    '15': { title: 'Best before', length: 6, kind: 'date' },
    '17': { title: 'Expiry date', length: 6, kind: 'date' },
    '21': { title: 'Serial number', max: 20, kind: 'alnum' },
    '30': { title: 'Variable count', max: 8, kind: 'numeric' },
    '37': { title: 'Count of trade items', max: 8, kind: 'numeric' },
    '310': { title: 'Net weight', length: 6, kind: 'measure', decimals: true, unit: 'kg' },
    '320': { title: 'Net weight', length: 6, kind: 'measure', decimals: true, unit: 'lb' },
    '400': { title: "Customer's purchase order", max: 30, kind: 'alnum' },
    '410': { title: 'Ship to GLN', length: 13, kind: 'gln' },
    '414': { title: 'Location GLN', length: 13, kind: 'gln' },
    '420': { title: 'Ship to postal code', max: 20, kind: 'alnum' },
};
/** GS1 mod-10 check digit for the digits before the last one. */
export function gs1CheckDigit(body) {
    let sum = 0;
    for (let i = 0; i < body.length; i++) {
        const d = body.charCodeAt(body.length - 1 - i) - 48;
        sum += d * (i % 2 === 0 ? 3 : 1);
    }
    return (10 - (sum % 10)) % 10;
}
export function isValidGs1Key(value, length) {
    if (!/^\d+$/.test(value) || (length !== undefined && value.length !== length))
        return false;
    return gs1CheckDigit(value.slice(0, -1)) === Number(value.at(-1));
}
/** GTIN-8/12/13/14 → GTIN-14 (left-padded) after validating the check digit. */
export function normalizeGtin(value) {
    const v = value.trim();
    if (!/^\d{8}$|^\d{12,14}$/.test(v) || !isValidGs1Key(v))
        return null;
    return v.padStart(14, '0');
}
/** Builds an SSCC from extension digit, company prefix and serial reference. */
export function buildSscc(extension, companyPrefix, serial) {
    const serialDigits = 16 - companyPrefix.length;
    const body = `${extension}${companyPrefix}${String(serial).padStart(serialDigits, '0')}`;
    if (body.length !== 17 || !/^\d+$/.test(body))
        throw new Error('Invalid SSCC components');
    return body + gs1CheckDigit(body);
}
function gs1Date(yymmdd) {
    const yy = Number(yymmdd.slice(0, 2));
    const mm = Number(yymmdd.slice(2, 4));
    let dd = Number(yymmdd.slice(4, 6));
    if (mm < 1 || mm > 12)
        return null;
    // GS1 §7.12: century chosen so the date falls within -49..+50 years of today.
    const now = new Date().getUTCFullYear();
    const base = now - (now % 100);
    let year = base + yy;
    if (year - now > 50)
        year -= 100;
    else if (now - year > 49)
        year += 100;
    const last = new Date(Date.UTC(year, mm, 0)).getUTCDate();
    if (dd === 0)
        dd = last; // DD=00 means last day of the month.
    if (dd > last)
        return null;
    return `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}
function specFor(data, i) {
    for (const len of [2, 3]) {
        const ai = data.slice(i, i + len);
        const spec = SPECS[ai];
        // Measure AIs (310n, 320n) are always four digits; the 4th is the decimal point.
        if (spec && !spec.decimals)
            return { ai, spec, decimals: 0 };
    }
    const four = data.slice(i, i + 4);
    const spec = SPECS[four.slice(0, 3)];
    if (spec?.decimals && /^\d$/.test(four[3] ?? ''))
        return { ai: four, spec, decimals: Number(four[3]) };
    return null;
}
function convert(ai, spec, raw, decimals, errors) {
    switch (spec.kind) {
        case 'gtin':
        case 'sscc':
        case 'gln':
            if (!isValidGs1Key(raw))
                errors.push(`AI (${ai}) ${spec.title}: check digit is wrong`);
            return raw;
        case 'date': {
            const d = /^\d{6}$/.test(raw) ? gs1Date(raw) : null;
            if (!d)
                errors.push(`AI (${ai}) ${spec.title}: "${raw}" is not a valid YYMMDD date`);
            return d ?? raw;
        }
        case 'numeric':
            if (!/^\d+$/.test(raw))
                errors.push(`AI (${ai}) ${spec.title}: must be digits`);
            return String(Number(raw));
        case 'measure': {
            if (!/^\d{6}$/.test(raw))
                errors.push(`AI (${ai}) ${spec.title}: must be 6 digits`);
            const n = Number(raw);
            return decimals ? (n / 10 ** decimals).toFixed(decimals) : String(n);
        }
        default:
            if (!/^[\x21-\x22\x25-\x2f\x30-\x39\x3a-\x3f\x41-\x5a\x5f\x61-\x7a]*$/.test(raw))
                errors.push(`AI (${ai}) ${spec.title}: contains characters outside GS1 set 82`);
            return raw;
    }
}
export function parseGs1(input) {
    const errors = [];
    const elements = [];
    let data = input.replace(/\r|\n/g, '');
    data = data.replace(/^\](C1|d2|Q3|e0|J1)/, '');
    if (data.startsWith('(')) {
        // Human-readable form: (AI)value(AI)value — brackets delimit fields.
        const re = /\((\d{2,4})\)([^(]*)/g;
        let m;
        let consumed = 0;
        while ((m = re.exec(data))) {
            consumed += m[0].length;
            const found = specFor(m[1], 0);
            if (!found || found.ai !== m[1]) {
                errors.push(`Unsupported AI (${m[1]})`);
                continue;
            }
            push(found.ai, found.spec, m[2].trim(), found.decimals);
        }
        if (consumed !== data.length)
            errors.push('Unrecognised text outside (AI) groups');
    }
    else {
        if (data.startsWith(GS))
            data = data.slice(1);
        let i = 0;
        while (i < data.length) {
            if (data[i] === GS) {
                i += 1;
                continue;
            }
            const found = specFor(data, i);
            if (!found) {
                errors.push(`Unknown or unsupported AI at position ${i + 1} ("${data.slice(i, i + 4)}…")`);
                break;
            }
            const { ai, spec, decimals } = found;
            i += ai.length;
            let raw;
            if (spec.length !== undefined) {
                raw = data.slice(i, i + spec.length);
                if (raw.length < spec.length)
                    errors.push(`AI (${ai}) ${spec.title}: expected ${spec.length} characters`);
                i += spec.length;
            }
            else {
                const end = data.indexOf(GS, i);
                raw = end === -1 ? data.slice(i) : data.slice(i, end);
                i = end === -1 ? data.length : end + 1;
                if (spec.max !== undefined && raw.length > spec.max) {
                    errors.push(`AI (${ai}) ${spec.title}: longer than ${spec.max} characters (missing FNC1 separator?)`);
                }
            }
            push(ai, spec, raw, decimals);
        }
    }
    function push(ai, spec, raw, decimals) {
        if (!raw) {
            errors.push(`AI (${ai}) ${spec.title}: empty`);
            return;
        }
        if (spec.length !== undefined && raw.length !== spec.length && !data.startsWith('('))
            return;
        if (spec.length !== undefined && raw.length !== spec.length) {
            errors.push(`AI (${ai}) ${spec.title}: expected ${spec.length} characters`);
            return;
        }
        const value = convert(ai, spec, raw, decimals, errors);
        elements.push({ ai, title: spec.title, raw, value, ...(spec.unit ? { unit: spec.unit } : {}) });
    }
    if (!elements.length && !errors.length)
        errors.push('No GS1 data found');
    const get = (...ais) => elements.find((e) => ais.includes(e.ai))?.value;
    const weight = elements.find((e) => e.ai.startsWith('310') || e.ai.startsWith('320'));
    const count = get('37', '30');
    return {
        ok: errors.length === 0,
        elements,
        errors,
        sscc: get('00'),
        gtin: get('01', '02'),
        lot: get('10'),
        serial: get('21'),
        expiry: get('17'),
        bestBefore: get('15'),
        count: count !== undefined ? Number(count) : undefined,
        netWeightGrams: weight
            ? Math.round(Number(weight.value) * (weight.unit === 'lb' ? 453.59237 : 1000))
            : undefined,
    };
}
//# sourceMappingURL=gs1.js.map